import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classes from './index.module.css';

function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

function describeArc(
  x = 20,
  y = 20,
  radius = 18,
  startAngle = 0,
  endAngle = 90
) {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  const d = [
    'M',
    start.x,
    start.y,
    'A',
    radius,
    radius,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y,
  ].join(' ');

  return d;
}

function useAnimate({ start, end, animateTime = 1000 }) {
  const ref = useRef();
  const requestRef = useRef();
  const intialRef = useRef();
  const [count, setCount] = useState(0);

  function animate(time) {
    if (ref.current !== undefined) {
      const deltaTime = time - ref.current;

      // Pass on a function to the setter of the state
      // to make sure we always have the latest state
      setCount((prevCount) => prevCount + deltaTime);
    } else {
      intialRef.current = time;
    }
    ref.current = time;

    const diff = time - intialRef.current;
    if (diff > animateTime) {
      return;
    }
    requestRef.current = requestAnimationFrame(animate);
  }

  useEffect(() => {
    if (start !== end) {
      requestRef.current = requestAnimationFrame(animate);
    }
    return () => {
      cancelAnimationFrame(requestRef.current);
    };
  }, [start, end]); // Make sure the effect runs only once

  const fraction = count / animateTime;

  const middle = start + fraction * (end - start);

  return {
    middle: count === 0 ? start : middle,
    fraction,
    count,
  };
}

let last = 0;

export default function CircularProgress({
  className = '',
  stepCount = 4,
  currentStep = 1,
  animateFromLast = false,
}) {
  const x = 33;
  const y = x;
  const radius = x - 3;
  const startAngle = 0;
  const endAngle = (currentStep / stepCount) * 360;
  const viewBox = x * 2;

  const startAnimation = animateFromLast ? last : endAngle;

  const animate = useAnimate({
    start: startAnimation,
    end: endAngle,
  });

  useEffect(
    () => () => {
      last = endAngle;
    },
    []
  );

  let { middle } = animate;

  if (middle >= 360) {
    middle = 359.99;
  }

  return (
    <div className={`${classes.progress} ${className}`}>
      <svg
        width={viewBox}
        height={viewBox}
        viewBox={`0 0 ${viewBox} ${viewBox}`}
        data-cy="circularProgress"
      >
        <path
          id="arc1"
          fill="none"
          strokeWidth={1}
          d={describeArc(x, y, radius - 1, startAngle, 359.99)}
        />
        <path
          id="arc1"
          fill="none"
          strokeWidth={4}
          d={describeArc(x, y, radius, startAngle, middle)}
        />
      </svg>
      <div className={classes.count}>{currentStep}</div>
    </div>
  );
}

CircularProgress.propTypes = {
  className: PropTypes.string,
  stepCount: PropTypes.number,
  currentStep: PropTypes.number,
};
